"use client";

import { useEffect, useRef, useState } from "react";
import { Share2, Link2, Mail, MessageCircle, Send } from "lucide-react";
import { share, type SharePayload } from "@/lib/share";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface ShareMenuProps {
  payload: SharePayload;
  className?: string;
  buttonClassName?: string;
  /** Render as an icon-only square button. */
  iconOnly?: boolean;
  label?: string;
  /** On devices with Web Share, launch the native sheet on the first tap. */
  oneTap?: boolean;
  /** Optional first-party event used to measure share intent without third-party pixels. */
  analytics?: {
    query: string;
    type: string;
    fromProductId?: string;
    fromProductName?: string;
  };
}

/**
 * Multi-platform share menu. On mobile the system share sheet is the
 * primary path (one tap, native UX); the explicit platform buttons stay
 * available as a fallback or for users who prefer choosing the channel.
 */
export function ShareMenu({
  payload,
  className,
  buttonClassName,
  iconOnly = false,
  label = "Compartir",
  oneTap = false,
  analytics,
}: ShareMenuProps) {
  const [open, setOpen] = useState(false);
  const [hasWebShare, setHasWebShare] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setHasWebShare(typeof navigator !== "undefined" && typeof navigator.share === "function");
  }, []);

  useEffect(() => {
    if (!open) return;
    function onDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function attributedUrl(channel: string) {
    try {
      const url = new URL(payload.url, window.location.origin);
      url.searchParams.set("utm_source", channel);
      url.searchParams.set("utm_medium", "share");
      url.searchParams.set("utm_campaign", "organic_share");
      return url.toString();
    } catch {
      return payload.url;
    }
  }

  const encodedText = encodeURIComponent(payload.text ?? payload.title ?? "");
  const encodedSubject = encodeURIComponent(payload.title ?? "");

  function trackShareIntent(channel: string) {
    if (!analytics) return;
    void fetch("/api/search/track", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        ...analytics,
        query: `${analytics.query} [${channel}]`,
      }),
      keepalive: true,
    }).catch(() => undefined);
  }

  const platforms: Array<{
    id: string;
    label: string;
    href: string;
    icon: React.ReactNode;
    tint: string;
  }> = [
    {
      id: "whatsapp",
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodedText}%20${encodeURIComponent(attributedUrl("whatsapp"))}`,
      icon: <MessageCircle className="w-4 h-4" />,
      tint: "text-emerald-600",
    },
    {
      id: "x",
      label: "X (Twitter)",
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(attributedUrl("x"))}&text=${encodedText}`,
      icon: <XLogo />,
      tint: "text-foreground",
    },
    {
      id: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(attributedUrl("facebook"))}`,
      icon: <FacebookLogo />,
      tint: "text-blue-600",
    },
    {
      id: "telegram",
      label: "Telegram",
      href: `https://t.me/share/url?url=${encodeURIComponent(attributedUrl("telegram"))}&text=${encodedText}`,
      icon: <Send className="w-4 h-4" />,
      tint: "text-sky-500",
    },
    {
      id: "email",
      label: "Email",
      href: `mailto:?subject=${encodedSubject}&body=${encodedText}%0A%0A${encodeURIComponent(attributedUrl("email"))}`,
      icon: <Mail className="w-4 h-4" />,
      tint: "text-rose-500",
    },
  ];

  async function copyLink() {
    trackShareIntent("copy");
    try {
      const url = attributedUrl("copy");
      await navigator.clipboard.writeText(url);
      toast({ title: "Enlace copiado", description: url, variant: "success", duration: 2500 });
    } catch {
      toast({ title: "No se pudo copiar", description: "Copia el enlace manualmente.", variant: "destructive" });
    }
    setOpen(false);
  }

  async function systemShare() {
    setOpen(false);
    trackShareIntent("system");
    await share({ ...payload, url: attributedUrl("system") });
  }

  function handlePrimaryClick() {
    if (oneTap && hasWebShare) {
      void systemShare();
      return;
    }
    setOpen((value) => !value);
  }

  return (
    <div ref={rootRef} className={cn("relative inline-block", className)}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={handlePrimaryClick}
        className={cn(
          "inline-flex items-center justify-center gap-1.5 rounded-lg border bg-background text-sm font-medium hover:bg-muted transition-colors",
          iconOnly ? "w-9 h-9" : "h-9 px-3",
          buttonClassName,
        )}
        title={label}
      >
        <Share2 className="w-4 h-4" />
        {!iconOnly && <span>{label}</span>}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-56 rounded-xl border bg-popover text-popover-foreground shadow-xl p-1.5 animate-in fade-in zoom-in-95"
        >
          {hasWebShare && (
            <button
              type="button"
              role="menuitem"
              onClick={systemShare}
              className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm hover:bg-muted text-left"
            >
              <Share2 className="w-4 h-4 text-primary" />
              <span className="font-medium">Compartir del sistema</span>
            </button>
          )}
          {platforms.map((p) => (
            <a
              key={p.id}
              role="menuitem"
              href={p.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackShareIntent(p.id);
                setOpen(false);
              }}
              className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm hover:bg-muted"
            >
              <span className={cn("flex items-center justify-center w-4 h-4", p.tint)}>{p.icon}</span>
              <span>{p.label}</span>
            </a>
          ))}
          <button
            type="button"
            role="menuitem"
            onClick={copyLink}
            className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm hover:bg-muted text-left border-t mt-1 pt-2"
          >
            <Link2 className="w-4 h-4 text-muted-foreground" />
            <span>Copiar enlace</span>
          </button>
        </div>
      )}
    </div>
  );
}

function XLogo() {
  return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
      <path d="M18.244 2H21l-6.51 7.44L22 22h-6.81l-4.78-6.27L4.8 22H2l7.02-8.02L2 2h6.91l4.35 5.74L18.244 2Zm-1.193 18.39h1.835L7.063 3.51H5.094l11.957 16.88Z" />
    </svg>
  );
}

function FacebookLogo() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
      <path d="M22 12.07C22 6.51 17.52 2 12 2S2 6.51 2 12.07c0 5.02 3.66 9.18 8.44 9.93v-7.02H7.9V12.07h2.54V9.85c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46H15.2c-1.24 0-1.63.77-1.63 1.56v1.89h2.77l-.44 2.91h-2.33V22c4.78-.75 8.43-4.91 8.43-9.93Z" />
    </svg>
  );
}
